import debounce from "lodash/debounce";
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import "./index.less";
import {
	getWindow,
	HighlightMaskProps,
	HightlightSpotHookProps,
	safe
} from "./interface";

const HighlightMask: React.FC<HighlightMaskProps> = ({
	id,
	sizes,
	onClick,
	isTransparent = true,
	maskOnClickableArea = false
}) => {
	const maskID = "mask_" + id;
	const clipID = "clip_" + id;
	const [px, py] = [0, 0];
	const [wpx, wpy] = [0, 0];
	const { w, h } = getWindow();
	const width = safe(sizes?.width + px * 2);
	const height = safe(sizes?.height + py * 2);
	const top = safe(sizes?.top - py - wpy / 2);
	const left = safe(sizes?.left - px - wpx / 2);
	const windowWidth = w - wpx;
	const windowHeight = h - wpy;

	const maskAreaStyles = {
		x: left,
		y: top,
		width,
		height,
		fill: "black",
		rx: 0
	};

	return (
		<div
			onClick={onClick}
			className={`highlight-mask-overlay ${
				isTransparent ? "opacity-0" : "opacity-25"
			}`}
		>
			<svg
				width={windowWidth}
				height={windowHeight}
				xmlns="http://www.w3.org/2000/svg"
				style={{
					width: windowWidth,
					height: windowHeight,
					left: Number(wpx) / 2,
					top: Number(wpy) / 2,
					position: "fixed"
				}}
			>
				<defs>
					{!maskOnClickableArea && (
						<mask id={maskID}>
							<rect
								x={0}
								y={0}
								width={windowWidth}
								height={windowHeight}
								fill="white"
							/>
							<rect
								style={maskAreaStyles}
								// Needs for Safari, as we pass any value, css rx will apply.
								rx={maskAreaStyles.rx ? 1 : undefined}
							/>
						</mask>
					)}
					{!maskOnClickableArea && (
						<clipPath id={clipID}>
							<polygon
								points={`0 0, 0 ${windowHeight}, ${left} ${windowHeight}, ${left} ${top}, ${
									left + width
								} ${top}, ${left + width} ${
									top + height
								}, ${left} ${
									top + height
								}, ${left} ${windowHeight}, ${windowWidth} ${windowHeight}, ${windowWidth} 0`}
							/>
						</clipPath>
					)}
				</defs>

				{/* The actual Mask */}
				<rect
					style={{
						top: 0,
						left: 0,
						width: windowWidth,
						height: windowHeight,
						fill: "currentColor",
						mask: `url(#${maskID})`
					}}
				/>
				{/* The clickable area */}
				<rect
					style={{
						left: 0,
						top: 0,
						width: windowWidth,
						height: windowHeight,
						fill: "currentcolor",
						pointerEvents: "auto",
						clipPath: `url(#${clipID})`
					}}
				/>
				<rect
					style={{
						left,
						top,
						width,
						height,
						pointerEvents: "auto",
						fill: "transparent",
						display: "none"
					}}
					// className={}
					// onClick={}
				/>
			</svg>
		</div>
	);
};

/**
 * This hook hide and show the highlighted spot mask for give element id and visibility
 * @param {HightlightSpotHookProps}
 * @return void
 */

export const useHighlightSpot = ({
	id,
	visible,
	isTransparent,
	query,
	maskOnClickableArea
}: HightlightSpotHookProps) => {
	function destroyOverlay() {
		const overlayElem = document.getElementById("overlay_" + id);
		if (overlayElem) {
			const unmountResult = ReactDOM.unmountComponentAtNode(overlayElem);
			if (unmountResult && overlayElem.parentNode) {
				overlayElem.parentNode.removeChild?.(overlayElem);
			}
		}
	}
	function showOverlay() {
		const elem = query
			? document.querySelector(query)
			: id && document.getElementById(id);
		if (id && elem) {
			destroyOverlay();
			const elemRect = elem.getBoundingClientRect();
			const overlayElem = document.createElement("div");
			overlayElem.id = "overlay_" + id;
			document.body.appendChild(overlayElem);
			ReactDOM.render(
				<HighlightMask
					id={id}
					sizes={elemRect}
					isTransparent={isTransparent}
					maskOnClickableArea={maskOnClickableArea}
				/>,
				overlayElem
			);
		}
	}
	const handleMaskVisibility = () => {
		if (id) {
			visible ? showOverlay() : destroyOverlay();
		}
	};

	const delayedOnWindowResize = debounce(handleMaskVisibility, 300);

	useEffect(() => {
		const onUnload = (_event: UIEvent) => delayedOnWindowResize();
		window.addEventListener("resize", onUnload);
		return () => window.removeEventListener("resize", onUnload);
	}, [delayedOnWindowResize]);

	useEffect(() => {
		handleMaskVisibility();
		// destroy overlay on unmount
		return () => {
			destroyOverlay();
		};
	}, [visible]);
};
